import { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { httpsCallable } from "firebase/functions";
import {
  selectClinicId,
  selectClinicProfile,
  setClinicProfile,
  selectTherapistProfile,
} from "../../store/slices/authSlice";
import { functions } from "../../firebase";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import { GlobalLoader } from "../shared";
import {
  X,
  CheckCircle,
  WarningCircle,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeSlash,
  Info,
  Plugs,
  Key,
  Gear,
  Check,
  Link as LinkIcon,
  UsersThree,
  Buildings,
  Receipt,
  CurrencyCircleDollar,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import morningLogo from "../../assets/morning_logo_minified.png";
import {
  MORNING_DOCUMENT_HINTS,
  MORNING_PAYMENT_TYPES,
  MORNING_BUSINESS_TYPES,
  getAllowedDocumentTypeOptions,
  getPaymentTypeOptions,
  getRecommendedDocumentType,
  MORNING_BUSINESS_TYPE_LABELS,
} from "../../services/morningService";
import { HelpButton } from "../shared";
import ModalCloseButton from "../ui/ModalCloseButton";
import "./MorningSetupWizard.css";
import LoaderLoop from "../shared/loader/LoaderLoop";

// Wizard steps configuration
const WIZARD_STEPS = [
  {
    id: "welcome",
    title: "ברוכים הבאים",
    subtitle: "חיבור למורנינג",
    icon: Plugs,
  },
  {
    id: "credentials",
    title: "פרטי חיבור",
    subtitle: "הזנת מפתח API",
    icon: Key,
  },
  {
    id: "preferences",
    title: "העדפות",
    subtitle: "הגדרות אחרונות",
    icon: Gear,
  },
  {
    id: "complete",
    title: "סיום",
    subtitle: "החיבור הושלם",
    icon: Check,
  },
];

function MorningSetupWizard({ isOpen, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const therapistProfile = useSelector(selectTherapistProfile);

  // Plan permissions hook
  const {
    checkMorning,
    isChecking: isCheckingPlanPermission,
    PlanLimitModalComponent,
  } = usePlanPermissions();

  // Track if we've checked permission for this open
  const [hasCheckedPermission, setHasCheckedPermission] = useState(false);
  const [isPermissionAllowed, setIsPermissionAllowed] = useState(true);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check plan permission when wizard opens
  useEffect(() => {
    const checkPermission = async () => {
      if (isOpen && !hasCheckedPermission) {
        setHasCheckedPermission(true);
        const allowed = await checkMorning();
        setIsPermissionAllowed(allowed);
        if (!allowed) {
          // The modal is shown automatically by the hook
          // Close the wizard after a small delay to let the modal appear
          setTimeout(() => {
            onClose();
          }, 100);
        }
      }
    };
    checkPermission();
  }, [isOpen, hasCheckedPermission, checkMorning, onClose]);

  // Reset permission check state when wizard closes
  useEffect(() => {
    if (!isOpen) {
      setHasCheckedPermission(false);
      setIsPermissionAllowed(true);
    }
  }, [isOpen]);

  // Form data - simplified, most values come from API
  const [formData, setFormData] = useState({
    apiKey: "",
    secret: "",
    // These will be auto-filled from API:
    businessType: MORNING_BUSINESS_TYPES.OSEK_MORSHE,
    vatIncluded: true,
    // User preferences (document type is resolved after validation: 320 / 400 for exempt):
    defaultDocumentType: getRecommendedDocumentType(null),
    defaultPaymentType: MORNING_PAYMENT_TYPES.CREDIT_CARD,
    allowTherapistsToCreateInvoices: false,
  });

  // UI state
  const [showSecret, setShowSecret] = useState(false);
  const [isCredentialsValidated, setIsCredentialsValidated] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [authMode, setAuthMode] = useState("oauth");

  // Auto-fill form data when business info is received
  useEffect(() => {
    if (businessInfo) {
      const allowed = getAllowedDocumentTypeOptions(businessInfo.allowedDocumentTypes, businessInfo.isExempt === true).map((o) => o.value);
      const recommended = getRecommendedDocumentType(businessInfo);
      setFormData((prev) => ({
        ...prev,
        businessType: businessInfo.businessType || prev.businessType,
        // Prices in CliniKit are gross → "includes VAT". Irrelevant for an exempt business.
        vatIncluded: true,
        defaultDocumentType: allowed.includes(recommended) ? recommended : allowed[0] || recommended,
      }));
    }
  }, [businessInfo]);

  // Handle form input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset validation when credentials change
    if (field === "apiKey" || field === "secret") {
      setIsCredentialsValidated(false);
      setBusinessInfo(null);
    }
    setError(null);
  }, []);

  // Validate credentials using Cloud Function
  const handleValidateCredentials = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const validateCredentials = httpsCallable(
        functions,
        "validateMorningCredentials"
      );
      const result = await validateCredentials({
        apiKey: formData.apiKey,
        secret: formData.secret,
      });

      if (result.data.success) {
        setIsCredentialsValidated(true);
        setBusinessInfo(result.data.businessInfo);
        setAuthMode(result.data.authMode === "legacy" ? "legacy" : "oauth");
      } else {
        setError(result.data.error || "אימות נכשל");
      }
    } catch (err) {
      console.error("[CK-8810] Error validating credentials:", {
        errorCode: "CK-8810",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה באימות הפרטים. נסה שוב.");
    } finally {
      setIsLoading(false);
    }
  };

  // Save integration settings using Cloud Function
  const handleSaveIntegration = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Save credentials securely via Cloud Function
      const saveCredentials = httpsCallable(functions, "saveMorningCredentials");
      const result = await saveCredentials({
        clinicId,
        apiKey: formData.apiKey,
        secret: formData.secret,
        preferences: {
          businessId: businessInfo?.businessId,
          businessName: businessInfo?.businessName,
          businessType: formData.businessType,
          exemption: businessInfo?.isExempt === true,
          vatRate: businessInfo?.vatRate ?? null,
          allowedDocumentTypes: businessInfo?.allowedDocumentTypes || [],
          receiptIncomeEnabled: businessInfo?.receiptIncomeEnabled === true,
          authMode,
          defaultDocumentType: formData.defaultDocumentType,
          defaultPaymentType: formData.defaultPaymentType,
          vatIncluded: formData.vatIncluded,
          allowTherapistsToCreateInvoices: formData.allowTherapistsToCreateInvoices,
        },
      });

      if (!result.data.success) {
        throw new Error(result.data.error || "שגיאה בשמירה");
      }

      // Update Redux state with the integration data returned from Cloud Function
      const morningIntegration = result.data.morningIntegration;
      dispatch(
        setClinicProfile({
          ...clinicProfile,
          isMorningPaymentSystemIntegrated: true,
          morningIntegration,
        })
      );

      // Move to success step
      setCurrentStep(3);

      // Notify parent
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("[CK-8810] Error saving integration:", {
        errorCode: "CK-8810",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה בשמירת ההגדרות. נסה שוב.");
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep === 1 && !isCredentialsValidated) {
      handleValidateCredentials();
      return;
    }

    if (currentStep === 2) {
      handleSaveIntegration();
      return;
    }

    setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setError(null);
  };

  const handleClose = () => {
    // Reset state
    setCurrentStep(0);
    setFormData({
      apiKey: "",
      secret: "",
      businessType: MORNING_BUSINESS_TYPES.OSEK_MORSHE,
      defaultDocumentType: getRecommendedDocumentType(null),
      defaultPaymentType: MORNING_PAYMENT_TYPES.CREDIT_CARD,
      vatIncluded: true,
      allowTherapistsToCreateInvoices: false,
    });
    setAuthMode("oauth");
    setIsCredentialsValidated(false);
    setBusinessInfo(null);
    setError(null);
    onClose();
  };

  // Check if can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return true;
      case 1:
        return formData.apiKey.length >= 10 && formData.secret.length >= 10;
      case 2:
        return isCredentialsValidated;
      default:
        return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderWelcomeStep();
      case 1:
        return renderCredentialsStep();
      case 2:
        return renderPreferencesStep();
      case 3:
        return renderCompleteStep();
      default:
        return null;
    }
  };

  // Step 1: Welcome
  const renderWelcomeStep = () => (
    <div className="wizard-step-content welcome-step">
      <div className="wizard-hero">
        <div className="hero-logo">
          <img src={morningLogo} alt="Morning" />
        </div>
        <h3>הפקת חשבוניות בלחיצת כפתור</h3>
        <p>
          חבר את CliniKit למורנינג והפק חשבוניות מס וקבלות ישירות מהמערכת
        </p>
      </div>

      <div className="features-grid">
        <div className="feature-card">
          <div className="feature-icon">
            <Receipt size={24} weight="duotone" />
          </div>
          <div className="feature-content">
            <h4>הפקה אוטומטית</h4>
            <p>חשבונית נוצרת אוטומטית לאחר קבלת תשלום</p>
          </div>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <CurrencyCircleDollar size={24} weight="duotone" />
          </div>
          <div className="feature-content">
            <h4>מעקב מרוכז</h4>
            <p>צפה בכל המסמכים במקום אחד</p>
          </div>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <ShieldCheck size={24} weight="duotone" />
          </div>
          <div className="feature-content">
            <h4>עמידה בתקנות</h4>
            <p>התאמה מלאה לדרישות רשות המסים</p>
          </div>
        </div>
      </div>

      <div className="wizard-info-banner">
        <Info size={20} weight="fill" />
        <div>
          <strong>לפני שמתחילים</strong>
          <p>
            ודא שיש לך חשבון מורנינג פעיל עם מפתח API.{" "}
            <a
              href="https://www.greeninvoice.co.il/help-center/generating-api-key/"
              target="_blank"
              rel="noopener noreferrer"
            >
              איך מקבלים מפתח?
            </a>
          </p>
        </div>
      </div>
    </div>
  );

  // Step 2: Credentials
  const renderCredentialsStep = () => (
    <div className="wizard-step-content credentials-step">
      <div className="wizard-section-header">
        <Key size={24} weight="duotone" />
        <div>
          <h4>הזנת פרטי גישה</h4>
          <p>המפתחות נמצאים בהגדרות מורנינג תחת "כלים למפתחים"</p>
        </div>
      </div>

      <div className="wizard-form">
        <div className="form-field">
          <label>
            <span className="label-text">מפתח API (API Key)</span>
            <span className="required-mark">*</span>
          </label>
          <input
            type="text"
            value={formData.apiKey}
            onChange={(e) => handleInputChange("apiKey", e.target.value)}
            placeholder="הכנס את מפתח ה-API"
            dir="ltr"
            className={error && !formData.apiKey ? "error" : ""}
          />
        </div>

        <div className="form-field">
          <label>
            <span className="label-text">Secret</span>
            <span className="required-mark">*</span>
          </label>
          <div className="input-with-action">
            <input
              type={showSecret ? "text" : "password"}
              value={formData.secret}
              onChange={(e) => handleInputChange("secret", e.target.value)}
              placeholder="הכנס את ה-Secret"
              dir="ltr"
              className={error && !formData.secret ? "error" : ""}
            />
            <button
              type="button"
              className="input-action-btn"
              onClick={() => setShowSecret(!showSecret)}
              title={showSecret ? "הסתר" : "הצג"}
            >
              {showSecret ? <EyeSlash size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
      </div>

      {isCredentialsValidated && businessInfo && (
        <div className="wizard-success-card">
          <div className="success-icon">
            <CheckCircle size={32} weight="fill" />
          </div>
          <div className="success-content">
            <h4>החיבור אומת בהצלחה!</h4>
            <div className="business-details">
              <div className="detail-item">
                <Buildings size={16} weight="fill" />
                <span>{businessInfo.businessName || clinicProfile?.name || "העסק שלך"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-badge">
                  {MORNING_BUSINESS_TYPE_LABELS[businessInfo.businessType] || businessInfo.businessType}
                </span>
                {businessInfo.isExempt && (
                  <span className="detail-badge exempt">פטור ממע"מ</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="wizard-error">
          <WarningCircle size={20} weight="fill" />
          <span>{error}</span>
        </div>
      )}

      <a
        href="https://www.greeninvoice.co.il/help-center/generating-api-key/"
        target="_blank"
        rel="noopener noreferrer"
        className="wizard-help-link"
      >
        <LinkIcon size={16} />
        <span>איך מוצאים את מפתח ה-API?</span>
      </a>
    </div>
  );

  // Step 3: Preferences - Simplified!
  const renderPreferencesStep = () => (
    <div className="wizard-step-content preferences-step">
      <div className="wizard-section-header">
        <Sparkle size={24} weight="duotone" />
        <div>
          <h4>כמעט סיימנו!</h4>
          <p>בחר את ברירות המחדל להפקת מסמכים</p>
        </div>
      </div>

      {/* Auto-detected business info display */}
      {businessInfo && (
        <div className="detected-info-card">
          <div className="detected-header">
            <CheckCircle size={18} weight="fill" />
            <span>זוהה אוטומטית מהחשבון שלך</span>
          </div>
          <div className="detected-items">
            <div className="detected-item">
              <span className="item-label">סוג עוסק:</span>
              <span className="item-value">
                {MORNING_BUSINESS_TYPE_LABELS[formData.businessType] || formData.businessType}
              </span>
            </div>
            <div className="detected-item">
              <span className="item-label">מע"מ:</span>
              <span className="item-value">
                {businessInfo.isExempt
                  ? "פטור ממע״מ"
                  : `חייב במע״מ${businessInfo.vatRate ? ` (${Math.round(businessInfo.vatRate * 100)}%)` : ""}`}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="wizard-form">
        <div className="form-field">
          <label>
            <span className="label-text">סוג מסמך ברירת מחדל</span>
          </label>
          <select
            value={formData.defaultDocumentType}
            onChange={(e) =>
              handleInputChange("defaultDocumentType", Number(e.target.value))
            }
          >
            {getAllowedDocumentTypeOptions(businessInfo?.allowedDocumentTypes, businessInfo?.isExempt === true).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="field-hint">
            {businessInfo?.isExempt
              ? "העסק פטור ממע״מ: יש להפיק קבלה. מורנינג לא מאפשר חשבונית מס לעוסק פטור."
              : MORNING_DOCUMENT_HINTS[formData.defaultDocumentType] ||
                "מומלץ: חשבונית מס / קבלה, חשבונית וקבלה במסמך אחד"}
          </span>
        </div>

        <div className="form-field">
          <label>
            <span className="label-text">שיטת תשלום ברירת מחדל</span>
          </label>
          <select
            value={formData.defaultPaymentType}
            onChange={(e) =>
              handleInputChange("defaultPaymentType", Number(e.target.value))
            }
          >
            {getPaymentTypeOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Therapist permissions toggle - Modern design */}
        <div className="permission-toggle-card">
          <div className="toggle-content">
            <div className="toggle-icon">
              <UsersThree size={24} weight="fill" />
            </div>
            <div className="toggle-text">
              <h5>אפשר למטפלים להפיק חשבוניות</h5>
              <p>
                כאשר מופעל, כל המטפלים בקליניקה יוכלו להפיק חשבוניות בשם העסק
              </p>
            </div>
            <div className="toggle-info">
              <Info size={16} weight="fill" className="info-icon" />
              <div className="toggle-tooltip">
                כאשר כבוי, רק בעל הקליניקה יוכל להפיק חשבוניות.
                ניתן לשנות זאת בכל עת בהגדרות.
              </div>
            </div>
          </div>
          <label className="modern-switch">
            <input
              type="checkbox"
              checked={formData.allowTherapistsToCreateInvoices}
              onChange={(e) =>
                handleInputChange("allowTherapistsToCreateInvoices", e.target.checked)
              }
            />
            <span className="switch-slider">
              <span className="switch-label">
                {formData.allowTherapistsToCreateInvoices ? "מופעל" : "כבוי"}
              </span>
            </span>
          </label>
        </div>
      </div>

      {error && (
        <div className="wizard-error">
          <WarningCircle size={20} weight="fill" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );

  // Step 4: Complete
  const renderCompleteStep = () => (
    <div className="wizard-step-content complete-step">
      <div className="success-hero">
        <div className="success-circle">
          <CheckCircle size={56} weight="fill" />
        </div>
        <h3>החיבור הושלם בהצלחה!</h3>
        <p>
          מורנינג מחובר כעת ל-CliniKit. תוכל להפיק חשבוניות ישירות מהמערכת.
        </p>
      </div>

      <div className="next-steps-card">
        <h4>מה עכשיו?</h4>
        <div className="steps-list">
          <div className="step-item">
            <div className="step-number">1</div>
            <div className="step-text">
              <strong>עבור לעמוד "ניהול מורנינג"</strong>
              <span>לצפייה בפאנל הניהול ויצירת חשבוניות</span>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">2</div>
            <div className="step-text">
              <strong>סמן תשלום כ"שולם"</strong>
              <span>ותוכל להפיק חשבונית ישירות</span>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">3</div>
            <div className="step-text">
              <strong>שנה הגדרות בכל עת</strong>
              <span>בהגדרות קליניקה &gt; אינטגרציות</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Always render the plan limit modal (it handles its own visibility)
  if (!isOpen) {
    return (
      <>
        {isCheckingPlanPermission && <GlobalLoader message="בודק הרשאות..." />}
        <PlanLimitModalComponent />
      </>
    );
  }

  return (
    <>
      {/* Global loader for permission check */}
      {isCheckingPlanPermission && <GlobalLoader message="בודק הרשאות..." />}

      {/* Plan limit modal */}
      <PlanLimitModalComponent />

      <div className="modal-overlay" onClick={handleClose}>
        <div className="morning-wizard-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="wizard-header">
            <div className="wizard-header-content">
              <img src={morningLogo} alt="Morning" className="header-logo" />
              <div className="wizard-title">
                <h2>{WIZARD_STEPS[currentStep].title}</h2>
              <span className="wizard-subtitle">
                {WIZARD_STEPS[currentStep].subtitle}
              </span>
            </div>
          </div>
          <div className="wizard-header-actions">
            <HelpButton featureKey="morning_setup" />
            <ModalCloseButton onClick={handleClose} />
          </div>
        </div>

        {/* Progress indicator */}
        <div className="wizard-progress">
          {WIZARD_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`progress-step ${index === currentStep ? "active" : ""} ${
                index < currentStep ? "completed" : ""
              }`}
            >
              <div className="step-indicator">
                {index < currentStep ? (
                  <Check size={14} weight="bold" />
                ) : (
                  <span className="step-num">{index + 1}</span>
                )}
              </div>
              <span className="step-label">{step.title}</span>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="wizard-body">{renderStepContent()}</div>

        {/* Footer */}
        <div className="wizard-footer">
          {currentStep > 0 && currentStep < 3 && (
            <button
              className="wizard-btn secondary"
              onClick={handleBack}
              disabled={isLoading}
            >
              <ArrowRight size={18} />
              <span>חזור</span>
            </button>
          )}

          {currentStep < 3 ? (
            <button
              className="wizard-btn primary"
              onClick={handleNext}
              disabled={!canProceed() || isLoading}
            >
              {isLoading ? (
                <>
                  <LoaderLoop size="xs" />
                  <span>
                    {currentStep === 1 && !isCredentialsValidated
                      ? "מאמת..."
                      : currentStep === 2
                      ? "שומר..."
                      : "טוען..."}
                  </span>
                </>
              ) : (
                <>
                  <span>
                    {currentStep === 0
                      ? "בוא נתחיל"
                      : currentStep === 1 && !isCredentialsValidated
                      ? "בדוק חיבור"
                      : currentStep === 1
                      ? "המשך"
                      : "סיים והתחבר"}
                  </span>
                  <ArrowLeft size={18} />
                </>
              )}
            </button>
          ) : (
            <button className="wizard-btn primary" onClick={handleClose}>
              <span>סגור</span>
            </button>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default MorningSetupWizard;
